import React, { forwardRef } from 'react';
import { cn } from './utils';

interface ScrollAreaProps {
  children: React.ReactNode;
  className?: string;
  type?: 'auto' | 'always' | 'scroll' | 'hover';
  id?: string;
  role?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

interface ScrollBarProps {
  orientation?: 'vertical' | 'horizontal';
  className?: string;
}

const ScrollArea = forwardRef<HTMLDivElement, ScrollAreaProps>(
  (
    { className, children, type = 'auto', id, role, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledby },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={cn('relative overflow-hidden', className)}
        id={id}
        role={role}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}>
        <div className="h-full w-full rounded-[inherit]">
          <div
            className={cn(
              'h-full w-full',
              type === 'always' && 'overflow-auto',
              type === 'scroll' && 'overflow-scroll',
              type === 'hover' && 'overflow-auto hover:overflow-auto',
              type === 'auto' && 'overflow-auto',
            )}
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: 'var(--border) transparent',
            }}>
            {children}
          </div>
        </div>
      </div>
    );
  },
);

ScrollArea.displayName = 'ScrollArea';

const ScrollBar = forwardRef<HTMLDivElement, ScrollBarProps>(({ className, orientation = 'vertical' }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        'flex touch-none select-none transition-colors',
        orientation === 'vertical' && 'absolute right-0 top-0 h-full w-2.5 border-l border-l-transparent',
        orientation === 'horizontal' && 'absolute bottom-0 left-0 h-2.5 w-full flex-col border-t border-t-transparent',
        className,
      )}>
      <div className="relative flex-1 rounded-full bg-border" />
    </div>
  );
});

ScrollBar.displayName = 'ScrollBar';

export { ScrollArea, ScrollBar };
